import { useEffect, useRef, useState } from 'react';
import type { Task } from '@map/shared';

/**
 * Does a rock match what was typed? Every word has to appear somewhere in the
 * title or the body, in any order and any case, so "invoice acme" finds the
 * task called "Acme" whose notes mention the invoice.
 */
export function matchesQuery(task: Task, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = `${task.title}\n${task.bodyMd ?? ''}`.toLowerCase();
  return words.every((word) => text.includes(word));
}

/**
 * Ctrl+F (⌘F) in the system view: narrows the sky to the rocks that match.
 *
 * The browser's own find cannot see into the canvas, so in the system view
 * the shortcut is taken over; inside a planet it is left alone. Planets are
 * never filtered — only the floating work. Escape (or the ×) clears the
 * search and puts every rock back.
 */
export function RockSearch({
  query,
  onQuery,
  shown,
  total,
}: {
  query: string;
  onQuery: (query: string) => void;
  shown: number;
  total: number;
}) {
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setOpen(true);
        // Already open: put the cursor back in it and select what is there.
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  // Leaving the system view (into a planet) closes the box, so it must not
  // leave a filter behind that nothing on screen admits to.
  useEffect(() => () => onQuery(''), [onQuery]);

  const close = () => {
    onQuery('');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="absolute left-1/2 top-16 z-30 flex w-[min(28rem,calc(100%-2rem))] -translate-x-1/2 items-center gap-2 rounded-[var(--radius-md)] px-3 py-2"
      style={{
        background: 'color-mix(in oklch, var(--surface-1) 94%, transparent)',
        boxShadow: 'var(--shadow-raised)',
      }}
    >
      <input
        ref={input}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            close();
          }
        }}
        placeholder="Find a task by title or notes"
        aria-label="Find a task"
        className="min-w-0 flex-1 bg-transparent text-sm outline-none"
      />
      <span className="shrink-0 text-xs text-text-muted" aria-live="polite">
        {query.trim() ? `${shown} of ${total}` : `${total} adrift`}
      </span>
      <button
        onClick={close}
        aria-label="Clear search"
        className="tap shrink-0 rounded-[var(--radius-sm)] px-1.5 text-text-muted hover:text-text"
      >
        ×
      </button>
    </div>
  );
}
